import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { HelmetProvider } from 'react-helmet-async'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'
import ScrollToTopButton from './components/ScrollToTopButton'
import LiveVisitorCounter from './components/LiveVisitorCounter'
import LoadingScreen from './components/LoadingScreen'
import ExitIntentPopup from './components/ExitIntentPopup'
import CookieConsent from './components/CookieConsent'
import ErrorBoundary from './components/ErrorBoundary'
import MaintenanceMode from './components/MaintenanceMode'
import Home from './pages/Home'
import About from './pages/About'
import Services from './pages/Services'
import Portfolio from './pages/Portfolio'
import Blog from './pages/Blog'
import BlogPost from './pages/BlogPost'
import Contact from './pages/Contact'
import Apply from './pages/Apply'
import Consultation from './pages/Consultation'
import LiveChat from './pages/LiveChat'
import AdminChat from './pages/AdminChat'
import AdminDashboard from './pages/AdminDashboard'
import StaffLogin from './pages/StaffLogin'
import CaseStudies from './pages/CaseStudies'
import PricingCalculator from './pages/PricingCalculator'
import FreeTools from './pages/FreeTools'
import Resources from './pages/Resources'
import IndustryRestaurants, { IndustryEcommerce, IndustryRealEstate, IndustryHealthcare, IndustryLegal, IndustryFitness } from './pages/industries/IndustryRestaurants'
import ProjectTracker from './pages/ProjectTracker'
import ReferralProgram from './pages/ReferralProgram'
import NotFound from './pages/NotFound'
import { LanguageProvider } from './context/LanguageContext'
import { ToastProvider } from './context/ToastContext'
import { initGA, usePageTracking } from './utils/analytics'

// 🔧 MAINTENANCE MODE - Ndrysho në false për të aktivizuar website-in normal
const MAINTENANCE_MODE = false
function AppContent() {
  const location = useLocation()
  
  // Initialize Google Analytics
  useEffect(() => {
    initGA()
  }, [])
  
  // Track page views
  usePageTracking()
  
  // Skip loading screen for admin routes - start with false for admin routes
  const isAdminRoute = location.pathname === '/staff-login' || location.pathname === '/dashboard-admin'
  const [loading, setLoading] = useState(!isAdminRoute)

  useEffect(() => {
    if (isAdminRoute) {
      setLoading(false)
      return
    }

    // Simulate loading time for public routes
    const timer = setTimeout(() => {
      setLoading(false)
    }, 3000) // 3 second loading screen

    return () => clearTimeout(timer)
  }, [isAdminRoute])

  // Show maintenance mode (except for admin routes)
  if (MAINTENANCE_MODE && !isAdminRoute) {
    return <MaintenanceMode key="maintenance" />
  }

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <LoadingScreen key="loading" />
      ) : (
        <motion.div
          key="app"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="min-h-screen flex flex-col text-cream"
        >
          {!isAdminRoute && <Navbar />}
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/pricing-calculator" element={<PricingCalculator />} />
              <Route path="/free-tools" element={<FreeTools />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/industries/restaurants" element={<IndustryRestaurants />} />
              <Route path="/industries/ecommerce" element={<IndustryEcommerce />} />
              <Route path="/industries/real-estate" element={<IndustryRealEstate />} />
              <Route path="/industries/healthcare" element={<IndustryHealthcare />} />
              <Route path="/industries/legal" element={<IndustryLegal />} />
              <Route path="/industries/fitness" element={<IndustryFitness />} />
              <Route path="/project-tracker" element={<ProjectTracker />} />
              <Route path="/referral" element={<ReferralProgram />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/consultation" element={<Consultation />} />
              <Route path="/live-chat" element={<LiveChat />} />
              <Route path="/staff-login" element={<StaffLogin />} />
              <Route path="/dashboard-admin" element={<AdminDashboard />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          {!isAdminRoute && <Footer />}
          <ScrollToTop />
          {!isAdminRoute && <ScrollToTopButton />}
          {!isAdminRoute && <LiveVisitorCounter />}
          {!isAdminRoute && <ExitIntentPopup />}
          {!isAdminRoute && <CookieConsent />}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <LanguageProvider>
          <ToastProvider>
            <div className="App bg-dark min-h-screen">
              <Router>
                <AppContent />
              </Router>
            </div>
          </ToastProvider>
        </LanguageProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App
